import * as React from 'react'
import ReactSelect from 'react-select'
import styled, { AnyStyledComponent } from 'styled-components'
import I18n from '../../../../core/i18n'
import { agentService } from '../../../../core/services'
import * as constants from '../../../../static/constants'
import {
  InputRadio,
  InputText,
  InputTextArea,
  Toggle,
  ReactSelectDateInput,
  SimpleDateInput,
} from '../../../atoms'
import AgentLayoutEdit from '../_layouts/edit'
import { IJsonResponse } from '../../../../core/JsonApiSerializer'
import SidebarLayout from '../../_layouts/sidebarLayout'
import moment from 'moment'
import MultipleDateRangeInput from 'components/molecules/RangeTagInput'
import TourSkillSelect from 'components/molecules/TourSkill/TourSkillSelect'
import { ILanguage, IVisaType, IGuideInfo, IBankAccount } from 'static/interfaces'

const FIELDS = {
  id: 'id',
  username: 'username',
}
const selectCustomStyles = {
  control: (provided, _) => ({
    ...provided,
    border: `1px solid ${constants.COLORS.divider}`,
    borderRadius: 4,
    '&:hover': { border: `1px solid ${constants.COLORS.primary}` },
  }),
  multiValue: (styles, { data }) => ({
    ...styles,
    backgroundColor: constants.COLORS.primary,
  }),
  multiValueLabel: (styles, { data }) => ({
    ...styles,
    color: constants.COLORS.white,
  }),
  multiValueRemove: (styles, { data }) => ({
    ...styles,
    color: constants.COLORS.white,
  }),
}

interface IProps {
  agent_skills: IJsonResponse
  agent_tour_skills: IJsonResponse
  guide: IJsonResponse
  guide_assign_skills: IJsonResponse
  guide_assign_tour_skills: IJsonResponse
  user: IJsonResponse
  agent_ranks: IJsonResponse
  agent_guide: IJsonResponse
  range_tags: IJsonResponse
  visa_types: IVisaType[]
  languages: ILanguage[]
}

interface IRangeTag {
  id: number
  name: string
  position: number
  start_time: string
  end_time: string
}

interface IRangeTagInput {
  id?: number
  value: string
  start: moment.Moment | undefined
  end: moment.Moment | undefined
}

const GENDER_OPTIONS = [
  { id: 'female', label: '女性', value: 'female' },
  { id: 'male', label: '男性', value: 'male' },
  { id: 'other', label: 'その他', value: 'other' },
]

const ACCOUNT_TYPES = [
  { id: 'saving', label: '普通', value: 'saving' },
  { id: 'checking', label: '当座', value: 'checking' },
]

const AgentGuideEdit: React.FC<IProps> = props => {
  const { data: user } = agentService.getDataFromJson(props.user)
  const { data: initialGuide } = agentService.getDataFromJson(props.guide)
  const isNew = !initialGuide.id
  const title = isNew
    ? I18n.t('admin.create_new', {
        model: I18n.t('guide', { scope: 'activerecord.models' }),
      })
    : I18n.t('admin.edit', {
        model: I18n.t('guide', { scope: 'activerecord.models' }),
      })
  const { data: allAgentSkills } = agentService.getDataFromJson(props.agent_skills)
  const { data: allAgentTourSkills } = agentService.getDataFromJson(props.agent_tour_skills)
  const { data: agentRanks } = agentService.getDataFromJson(props.agent_ranks)
  const { data: agentGuide } = agentService.getDataFromJson(props.agent_guide)
  const { data: rangeTags }: { data: IRangeTag[] } = agentService.getDataFromJson(props.range_tags)
  const { data: visaTypes } = agentService.getDataFromJson(props.visa_types)
  const { data: languages } = agentService.getDataFromJson(props.languages)
  const { data: initialGuideAssignTourSkills } = agentService.getDataFromJson(
    props.guide_assign_tour_skills || { data: [] }
  )
  const { data: initialGuideAssignSkills } = agentService.getDataFromJson(
    props.guide_assign_skills || { data: [] }
  )
  const [agentRankId, setAgentRankId] = React.useState(agentGuide.agent_rank?.id)
  const [guide, setGuide] = React.useState(initialGuide)
  const [selectedTourSkills, setSelectedTourSkills] = React.useState(initialGuideAssignTourSkills)
  const [guideInfo, setGuideInfo] = React.useState<IGuideInfo>(initialGuide.guide_info)
  const [bankAccount, setBankAccount] = React.useState<IBankAccount>(initialGuide.bank_account)
  const [agentSkills] = React.useState(allAgentSkills.filter(skill => skill.agent_id === user.id))
  const [agentTourSkills] = React.useState(
    allAgentTourSkills.filter(tourSkill => tourSkill.agent_id === user.id)
  )
  const [selectedSkills, setSelectedSkills] = React.useState(
    initialGuideAssignSkills.length
      ? initialGuideAssignSkills.map(skill => ({
          value: String(skill.agent_skill_id),
          label: agentSkills.find(agentSkill => agentSkill.id === skill.agent_skill_id)?.name || '',
        }))
      : []
  )
  const [selectedLanguages, setSelectedLanguages] = React.useState(
    initialGuide.languages.length
      ? initialGuide.languages.map(lang => ({
          value: String(lang.id),
          label: languages.find(language => language.id === lang.id)?.name || '',
        }))
      : []
  )

  const initialRangeTags: IRangeTagInput[] = React.useMemo(() => {
    if (initialGuide.guide_range_tags.length) {
      return initialGuide.guide_range_tags.reduce((prev, guideRangeTag) => {
        const index = rangeTags.findIndex(({ id }) => id === guideRangeTag.range_tag_id)
        return index !== -1
          ? [
              ...prev,
              {
                id: guideRangeTag.id,
                value: String(rangeTags[index].id),
                label: rangeTags[index].name,
                start: guideRangeTag.start_date ? moment(guideRangeTag.start_date) : undefined,
                end: guideRangeTag.end_date ? moment(guideRangeTag.end_date) : undefined,
              },
            ]
          : prev
      }, [])
    }
    return []
  }, [])

  const [selectedRangeTags, setSelectedRangeTags] = React.useState<IRangeTagInput[]>(
    initialRangeTags
  )

  const handleSubmit = React.useCallback(async () => {
    const params = {
      guide_attributes: {
        id: initialGuide.id,
        email: guide.email,
        tel: guide.tel,
        birthday: guide.birthday?.format('YYYY-MM-DD'),
        fullname: guide.fullname,
        username: guide.username,
        gender: guide.gender,
        guide_search_text: guide.guide_search_text,
        internal_memo: guide.internal_memo,
        status: guide.status,
        language_ids: selectedLanguages.map(lang => lang.value),
        assigned_skill_ids: selectedSkills.map(skill => skill.value),
        assigned_tour_skill_ids: selectedTourSkills.map(skill => skill.value),
      },
      guide_info_attributes: {
        bio: guideInfo?.bio,
        interpreter_guide_qualification: guideInfo?.interpreter_guide_qualification,
        visa_type_id: guideInfo?.visa_type_id,
        visa_expires_at: guideInfo?.visa_expires_at,
        has_my_number: guideInfo?.has_my_number,
      },
      bank_account_attributes: {
        invoice_number: bankAccount?.invoice_number,
        recipient_name: bankAccount?.recipient_name,
        is_company: bankAccount?.is_company,
        account_name: bankAccount?.account_name,
        account_number: bankAccount?.account_number,
        account_type: bankAccount?.account_type,
        bank_name: bankAccount?.bank_name,
        branch_name: bankAccount?.branch_name,
      },
      assign_guide_tour_skills_attributes: selectedTourSkills,
      guide_range_tags_attributes: selectedRangeTags.map(tag => {
        return {
          id: tag.id,
          range_tag_id: tag.value,
          start_date: tag.start?.format('YYYY-MM-DD'),
          end_date: tag.end?.format('YYYY-MM-DD'),
        }
      }),
      agent_rank_id: agentRankId,
    }
    try {
      if (isNew) {
        await agentService.createGuide(params)
      } else {
        await agentService.updateGuide(initialGuide.id, params)
      }
      location.href = '/agent/guides'
    } catch (e) {
      console.error(e)
    }
  }, [
    guide,
    selectedSkills,
    selectedTourSkills,
    agentRankId,
    selectedRangeTags,
    selectedLanguages,
    guideInfo,
    bankAccount,
    isNew,
  ])
  return (
    <SidebarLayout user={user}>
      <S.Wrapper>
        <AgentLayoutEdit
          model="guides"
          title={title}
          fields={FIELDS}
          hideAgentNav={true}
          indexLink="/agent/guides"
          buttonText={isNew ? I18n.t('generic.create') : I18n.t('generic.update')}
          formItems={
            <>
              <InputText readonly={true} name="id" defaultValue={initialGuide.id} label="ID" />
              <InputText
                required={true}
                name="username"
                defaultValue={initialGuide.username}
                label={`${I18n.t('generic.username')} (${I18n.t('generic.nickname')})`}
                onChangeHandler={e => setGuide({ ...guide, username: e.target.value })}
              />
              <InputText
                required={true}
                name="fullname"
                defaultValue={initialGuide.fullname}
                label={I18n.t('generic.fullname')}
                onChangeHandler={e => setGuide({ ...guide, fullname: e.target.value })}
              />
              <S.InputWrapper fullWidth={true}>
                <InputText
                  required={true}
                  name="email"
                  defaultValue={initialGuide.email}
                  label={I18n.t('generic.email')}
                  onChangeHandler={e => setGuide({ ...guide, email: e.target.value })}
                />
              </S.InputWrapper>
              {!isNew && (
                <>
                  <S.InputWrapper>
                    <InputText
                      name="tel"
                      type="tel"
                      defaultValue={initialGuide.tel}
                      label={I18n.t('generic.tel')}
                      onChangeHandler={e => setGuide({ ...guide, tel: e.target.value })}
                    />
                  </S.InputWrapper>
                  <S.InputWrapper>
                    <SimpleDateInput
                      label={I18n.t('generic.birthday')}
                      name="birthday"
                      defaultDate={
                        initialGuide.birthday
                          ? moment(initialGuide.birthday)
                          : moment().subtract(20, 'years')
                      }
                      onChangeHandler={date => setGuide({ ...guide, birthday: date })}
                    />
                  </S.InputWrapper>
                  <S.InputWrapper>
                    <InputRadio
                      name="gender"
                      defaultValue={initialGuide.gender ?? '0'}
                      label={I18n.t('generic.gender')}
                      onChangeHandler={e => setGuide({ ...guide, gender: e.target.value })}
                      options={GENDER_OPTIONS}
                    />
                  </S.InputWrapper>
                  <S.InputAreaWrapper>
                    <InputTextArea
                      name="internal_memo"
                      defaultValue={guide.internal_memo}
                      label={I18n.t('generic.memo')}
                      onChangeHandler={e => setGuide({ ...guide, internal_memo: e.target.value })}
                      fullWidth={true}
                    />
                  </S.InputAreaWrapper>
                  <S.InputWrapper>
                    <InputText
                      name="guide_search_text"
                      defaultValue={guide.guide_search_text}
                      label={I18n.t('generic.guide_search_text')}
                      onChangeHandler={e =>
                        setGuide({ ...guide, guide_search_text: e.target.value })
                      }
                    />
                  </S.InputWrapper>
                  <S.ReactSelectWrapper>
                    <p className="ReactSelect_Title">{I18n.t('generic.skill')}</p>
                    <div className="ReactSelect_Main">
                      <ReactSelect
                        placeholder={I18n.t('generic.select_skill')}
                        options={agentSkills.map(skill => ({
                          value: String(skill.id),
                          label: skill.name,
                        }))}
                        isMulti={true}
                        onChange={setSelectedSkills}
                        styles={selectCustomStyles}
                        defaultValue={selectedSkills}
                        menuPortalTarget={document.querySelector('body')}
                      />
                    </div>
                  </S.ReactSelectWrapper>
                  <TourSkillSelect
                    tourSkills={selectedTourSkills}
                    setTourSkills={setSelectedTourSkills}
                    agentTourSkills={agentTourSkills}
                  />
                  <S.ReactSelectWrapper>
                    <p className="ReactSelect_Title">{I18n.t('generic.rank')}</p>
                    <div className="ReactSelect_Main">
                      <ReactSelect
                        placeholder={I18n.t('generic.rank')}
                        options={agentRanks.map(agentRank => ({
                          value: agentRank.id,
                          label: agentRank.name_i18n,
                        }))}
                        isMulti={false}
                        onChange={option => setAgentRankId(option.value)}
                        defaultValue={{
                          label: agentGuide.agent_rank?.name_i18n,
                          value: agentGuide.agent_rank?.id,
                        }}
                        menuPortalTarget={document.querySelector('body')}
                      />
                    </div>
                  </S.ReactSelectWrapper>
                  <S.ReactSelectWrapper>
                    <p className="ReactSelect_Title">{I18n.t('generic.range_tag')}</p>
                    <div className="ReactSelect_Main">
                      <MultipleDateRangeInput
                        rangeTags={rangeTags.map(tag => ({
                          value: String(tag.id),
                          label: tag.name,
                        }))}
                        selectedRangeTags={selectedRangeTags}
                        setSelectedRangeTags={setSelectedRangeTags}
                      />
                    </div>
                  </S.ReactSelectWrapper>
                  <S.InputAreaWrapper>
                    <InputTextArea
                      name="bio"
                      defaultValue={guideInfo?.bio}
                      label={I18n.t('guide_info.bio', { scope: 'activerecord.attributes' })}
                      onChangeHandler={e => setGuideInfo({ ...guideInfo, bio: e.target.value })}
                      fullWidth={true}
                    />
                  </S.InputAreaWrapper>
                  <S.ReactSelectWrapper>
                    <p className="ReactSelect_Title">
                      {I18n.t('guide_info.interpreter_guide_qualification', {
                        scope: 'activerecord.attributes',
                      })}
                    </p>
                    <div className="toggleContainer">
                      <Toggle
                        name="interpreter_guide_qualification"
                        checked={guideInfo?.interpreter_guide_qualification}
                        onChangeHandler={e =>
                          setGuideInfo({
                            ...guideInfo,
                            interpreter_guide_qualification: e.target.checked,
                          })
                        }
                        hideState={true}
                      />
                      <div className="toggleState">
                        {guideInfo?.interpreter_guide_qualification ? 'あり' : 'なし'}
                      </div>
                    </div>
                  </S.ReactSelectWrapper>
                  <S.ReactSelectWrapper>
                    <p className="ReactSelect_Title">
                      {I18n.t('guide_language', { scope: 'activerecord.models' })}
                    </p>
                    <div className="ReactSelect_Main">
                      <ReactSelect
                        placeholder={I18n.t('generic.select_language')}
                        options={languages.map(lang => ({
                          value: String(lang.id),
                          label: lang.name,
                        }))}
                        isMulti={true}
                        onChange={setSelectedLanguages}
                        styles={selectCustomStyles}
                        defaultValue={selectedLanguages}
                        menuPortalTarget={document.querySelector('body')}
                      />
                    </div>
                  </S.ReactSelectWrapper>
                  <S.ReactSelectWrapper>
                    <p className="ReactSelect_Title">
                      {I18n.t('guide_info.has_my_number', { scope: 'activerecord.attributes' })}
                    </p>
                    <div className="toggleContainer">
                      <Toggle
                        name="has_my_number"
                        checked={guideInfo?.has_my_number}
                        onChangeHandler={e =>
                          setGuideInfo({ ...guideInfo, has_my_number: e.target.checked })
                        }
                        hideState={true}
                      />
                      <div className="toggleState">
                        {guideInfo?.has_my_number ? 'あり' : 'なし'}
                      </div>
                    </div>
                  </S.ReactSelectWrapper>
                  <S.InputWrapper fullWidth={true}>
                    <InputText
                      name="bank_name"
                      defaultValue={bankAccount?.bank_name}
                      label={I18n.t('bank_account.bank_name', { scope: 'activerecord.attributes' })}
                      onChangeHandler={e =>
                        setBankAccount({ ...bankAccount, bank_name: e.target.value })
                      }
                    />
                  </S.InputWrapper>
                  <S.InputWrapper>
                    <InputRadio
                      name="account_type"
                      defaultValue={bankAccount?.account_type ?? '0'}
                      label={I18n.t('bank_account.account_type', {
                        scope: 'activerecord.attributes',
                      })}
                      onChangeHandler={e =>
                        setBankAccount({ ...bankAccount, account_type: e.target.value })
                      }
                      options={ACCOUNT_TYPES}
                    />
                  </S.InputWrapper>
                  <S.InputWrapper fullWidth={true}>
                    <InputText
                      name="branch_name"
                      defaultValue={bankAccount?.branch_name}
                      label={I18n.t('bank_account.branch_name', {
                        scope: 'activerecord.attributes',
                      })}
                      onChangeHandler={e =>
                        setBankAccount({ ...bankAccount, branch_name: e.target.value })
                      }
                    />
                  </S.InputWrapper>
                  <S.InputWrapper fullWidth={true}>
                    <InputText
                      name="account_number"
                      defaultValue={bankAccount?.account_number}
                      label={I18n.t('bank_account.account_number', {
                        scope: 'activerecord.attributes',
                      })}
                      onChangeHandler={e =>
                        setBankAccount({ ...bankAccount, account_number: e.target.value })
                      }
                    />
                  </S.InputWrapper>
                  <S.InputWrapper fullWidth={true}>
                    <InputText
                      name="account_name"
                      defaultValue={bankAccount?.account_name}
                      label={I18n.t('bank_account.account_name', {
                        scope: 'activerecord.attributes',
                      })}
                      onChangeHandler={e =>
                        setBankAccount({ ...bankAccount, account_name: e.target.value })
                      }
                    />
                  </S.InputWrapper>
                  <S.InputWrapper>
                    <InputText
                      name="invoice_number"
                      defaultValue={bankAccount?.invoice_number}
                      label={I18n.t('bank_account.invoice_number', {
                        scope: 'activerecord.attributes',
                      })}
                      onChangeHandler={e =>
                        setBankAccount({ ...bankAccount, invoice_number: e.target.value })
                      }
                    />
                  </S.InputWrapper>
                  <S.ReactSelectWrapper>
                    <p className="ReactSelect_Title">
                      {I18n.t('bank_account.is_company', { scope: 'activerecord.attributes' })}
                    </p>
                    <div className="toggleContainer">
                      <Toggle
                        name="is_company"
                        checked={bankAccount?.is_company}
                        onChangeHandler={e =>
                          setBankAccount({ ...bankAccount, is_company: e.target.checked })
                        }
                        hideState={true}
                      />
                      <div className="toggleState">{bankAccount?.is_company ? '法人' : '個人'}</div>
                    </div>
                  </S.ReactSelectWrapper>
                  <S.ReactSelectWrapper>
                    <p className="ReactSelect_Title">
                      {I18n.t('visa_type', { scope: 'activerecord.models' })}
                    </p>
                    <div className="ReactSelect_Main">
                      <ReactSelect
                        placeholder={I18n.t('generic.select_visa_type')}
                        options={[{ value: null, label: '' }].concat(
                          visaTypes.map(visaType => ({
                            value: visaType.id,
                            label: visaType.name_ja,
                          }))
                        )}
                        isMulti={false}
                        onChange={option =>
                          setGuideInfo({ ...guideInfo, visa_type_id: option?.value ?? null })
                        }
                        value={
                          guideInfo?.visa_type_id
                            ? {
                                label:
                                  visaTypes.find(visaType => visaType.id === guideInfo.visa_type_id)
                                    ?.name_ja || '',
                                value: guideInfo.visa_type_id,
                              }
                            : null
                        }
                        menuPortalTarget={document.querySelector('body')}
                      />
                    </div>
                  </S.ReactSelectWrapper>
                  {guideInfo?.visa_type_id && (
                    <S.ReactSelectWrapper>
                      <p className="ReactSelect_Title">
                        {I18n.t('guide_info.visa_expires_at', { scope: 'activerecord.attributes' })}
                      </p>
                      <div className="ReactSelect_Main">
                        <ReactSelectDateInput
                          name="visa_expires_at"
                          defaultDate={
                            guideInfo?.visa_expires_at ? moment(guideInfo.visa_expires_at) : null
                          }
                          onChangeHandler={date => {
                            setGuideInfo({
                              ...guideInfo,
                              visa_expires_at: date ? date.format('YYYY-MM-DD') : null,
                            })
                          }}
                          minYear={moment().year() - 50}
                          maxYear={moment().year() + 50}
                          customStyles={selectCustomStyles}
                        />
                      </div>
                    </S.ReactSelectWrapper>
                  )}
                  <S.ReactSelectWrapper>
                    <p className="ReactSelect_Title">
                      {I18n.t('user.status', {
                        scope: 'activerecord.attributes',
                      })}
                    </p>
                    <div className="ReactSelect_Main">
                      <ReactSelect
                        options={constants.GUIDE_STATUSES.map(status => ({
                          value: status,
                          label: I18n.t(`enums.user.status.${status}`),
                        }))}
                        isMulti={false}
                        isClearable={false}
                        onChange={option => {
                          if (option.value === guide.status) {
                            return
                          }
                          // 論理削除・削除からの復帰は誤操作防止のため確認する
                          if (
                            option.value === 'deleted' &&
                            !window.confirm(I18n.t('generic.delete_confirmation'))
                          ) {
                            return
                          }
                          if (
                            guide.status === 'deleted' &&
                            !window.confirm(
                              I18n.t(
                                option.value === 'active'
                                  ? 'generic.activate_confirmation'
                                  : 'generic.inactivate_confirmation'
                              )
                            )
                          ) {
                            return
                          }
                          setGuide({ ...guide, status: option.value })
                        }}
                        value={{
                          value: guide.status,
                          label: I18n.t(`enums.user.status.${guide.status}`),
                        }}
                        styles={selectCustomStyles}
                        menuPortalTarget={document.querySelector('body')}
                      />
                    </div>
                  </S.ReactSelectWrapper>
                </>
              )}
            </>
          }
          handleSubmit={handleSubmit}
        />
      </S.Wrapper>
    </SidebarLayout>
  )
}
const S: { [key: string]: AnyStyledComponent } = {}

S.Wrapper = styled.div`
  .Index_Main {
    padding: 0 !important;
  }
  .toggleContainer {
    display: flex;
    align-items: center;
    gap: 8px;
  }
`

S.InputWrapper = styled.div<{ fullWidth?: boolean }>`
  display: flex;
  margin-top: 24px;
  .InputText {
    ${props => props.fullWidth && 'width: 100%;'}
  }
  @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
    flex-direction: column;
  }
`
S.InputAreaWrapper = styled.div`
  margin-top: 24px;
  textarea {
    height: 80px;
  }
`

S.ReactSelectWrapper = styled.div`
  display: flex;
  margin-top: 24px;
  @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
    flex-direction: column;
  }
  .ReactSelect_Title {
    min-width: 180px;
    max-width: 180px;
    font-size: 15px;
    font-weight: bold;
    line-height: 1;
    margin-top: 8px;
    white-space: normal;
    word-wrap: break-word;
    overflow-wrap: break-word;

    @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
      width: 100%;
      max-width: unset;
      margin-bottom: 12px;
    }
  }
  .ReactSelect_Main {
    flex: 1;
    font-size: 15px;
  }
  .RangeTag_Wrapper {
    width: 100%;
  }
`

S.TourSkillWrapper = styled.div``

export default AgentGuideEdit
